import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { protocolService } from '../services/protocolService'
import { ProtocolFormData } from '../types/protocol'
import './ProtocolNew.css'

export function ProtocolNew() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<ProtocolFormData>({
    name: '',
    description: '',
    status: 'draft',
    criteria: [],
    rules: [],
    category: '',
    tags: [],
    version: '1.0',
  })

  const createMutation = useMutation({
    mutationFn: (data: ProtocolFormData) => protocolService.create(data),
    onSuccess: (protocol) => {
      queryClient.invalidateQueries({ queryKey: ['protocols'] })
      navigate(`/protocols/${protocol.id}`)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createMutation.mutate(formData)
  }

  return (
    <div className="protocol-new-page">
      <div className="page-header">
        <h1>Create New Protocol</h1>
        <button className="btn-secondary" onClick={() => navigate('/protocols')}>
          Cancel
        </button>
      </div>

      <form onSubmit={handleSubmit} className="protocol-form">
        <div className="form-group">
          <label htmlFor="name">
            Protocol Name <span className="required">*</span>
          </label>
          <input
            id="name"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            placeholder="e.g., Diabetes Management Protocol"
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={4}
            placeholder="Describe the purpose and scope of this protocol"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="category">Category</label>
            <input
              id="category"
              type="text"
              value={formData.category || ''}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              placeholder="e.g., Chronic Disease Management"
            />
          </div>

          <div className="form-group">
            <label htmlFor="version">Version</label>
            <input
              id="version"
              type="text"
              value={formData.version || '1.0'}
              onChange={(e) => setFormData({ ...formData, version: e.target.value })}
              placeholder="1.0"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="effectiveDate">Effective Date</label>
          <input
            id="effectiveDate"
            type="date"
            value={formData.effectiveDate || ''}
            onChange={(e) => setFormData({ ...formData, effectiveDate: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label htmlFor="expiryDate">Expiry Date</label>
          <input
            id="expiryDate"
            type="date"
            value={formData.expiryDate || ''}
            onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
          />
        </div>

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate('/protocols')}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creating...' : 'Create Protocol'}
          </button>
        </div>
      </form>
    </div>
  )
}
